import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StateDistrictSelect from "@/components/StateDistrictSelect";
import { UserPlus, ArrowLeft, ShieldCheck, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "", email_local: "", password: "",
  phone: "", state: "", district: "", city: "",
};

export default function IBORegister() {
  const [form, setForm] = useState(empty);
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/ibo/register-config`)
      .then(({ data }) => setDomain(data.email_domain || ""))
      .catch(() => setDomain("whateverittakes.shop"));
  }, []);

  const setLocal = (v) => {
    // Strip any illegal chars in real time so the input stays clean.
    const cleaned = v.toLowerCase().replace(/[^a-z0-9._%+\-]/g, "");
    setForm((f) => ({ ...f, email_local: cleaned }));
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email_local.trim()) return toast.error("Email prefix is required");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!form.phone.trim()) return toast.error("Phone is required");
    if (!form.state) return toast.error("Please select your state");
    if (!form.district) return toast.error("Please select your district");
    if (!form.city.trim()) return toast.error("City is required");

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ibo/register-request`, form);
      setSubmitted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-8 space-y-4 text-center" data-testid="ibo-register-success">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-display font-bold text-2xl">Application submitted</h1>
          <p className="text-sm text-muted-foreground">
            Thanks {form.name.split(" ")[0]}. Your IBO application is now under review.
          </p>
          <div className="inline-flex items-center gap-2 text-sm bg-primary/5 border border-primary/20 rounded-md px-3 py-2">
            <Mail className="w-4 h-4 text-primary" />
            <span className="font-mono">{submitted.email}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            You'll be able to log in once an admin approves your account. This usually happens within 1 business day.
          </p>
          <div className="pt-2 flex gap-2 justify-center">
            <Button variant="outline" asChild><Link to="/login">Go to Login</Link></Button>
            <Button asChild><Link to="/">Home</Link></Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <span className="ml-auto text-sm font-medium">BDM Order Management</span>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-4 py-8 md:py-12">
        <div className="text-center space-y-2 mb-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
            <ShieldCheck className="w-3.5 h-3.5" /> IBO Application
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
            Register as an IBO
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Fill out the form below. Once an admin approves your application, you'll be able to log in and create auto-approved B2B bills.
          </p>
        </div>

        <Card className="p-6 md:p-8 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1 md:col-span-2">
              <Label>Full Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ravi Kumar"
                data-testid="ibo-register-name"
              />
            </div>

            <div className="space-y-1 md:col-span-2">
              <Label>Email <span className="text-red-500">*</span></Label>
              <div className="flex items-stretch rounded-md border border-input bg-white overflow-hidden focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
                <input
                  type="text"
                  value={form.email_local}
                  onChange={(e) => setLocal(e.target.value)}
                  placeholder="ravi"
                  className="flex-1 min-w-0 px-3 py-2 text-sm outline-none bg-transparent"
                  data-testid="ibo-register-email-local"
                  autoComplete="username"
                />
                <span className="flex items-center px-3 bg-muted text-sm text-muted-foreground select-none font-mono shrink-0" data-testid="ibo-register-email-domain">
                  @{domain || "whateverittakes.shop"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Only letters, numbers and <code>. _ % + -</code> allowed. Domain is fixed.
              </p>
            </div>

            <div className="space-y-1">
              <Label>Password <span className="text-red-500">*</span></Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="min 6 characters"
                data-testid="ibo-register-password"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-1">
              <Label>Phone <span className="text-red-500">*</span></Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="10-digit number"
                data-testid="ibo-register-phone"
              />
            </div>

            <div className="md:col-span-2">
              <StateDistrictSelect
                state={form.state}
                district={form.district}
                onStateChange={(v) => setForm({ ...form, state: v })}
                onDistrictChange={(v) => setForm({ ...form, district: v })}
                required
                testidPrefix="ibo-register"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>City <span className="text-red-500">*</span></Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="e.g. Bengaluru"
                data-testid="ibo-register-city"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={submit}
              disabled={loading}
              className="sm:flex-1"
              data-testid="ibo-register-submit"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              {loading ? "Submitting…" : "Submit Application"}
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")}>
              Already have an account? Log in
            </Button>
          </div>
        </Card>
      </main>
    </div>
  );
}
